import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area,
  RadialBarChart, RadialBar, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import api from "@/lib/api";
import { Users, TrendingUp, CalendarDays, Loader2 } from "lucide-react";

/* -------------------------------------------------------------------------
   Community dashboard charts.
   - Top-contributor chart intentionally removed (Feb 2026).
   - Remaining charts get vivid gradients, big hero-number readouts, ring
     dividers and staggered entrance animations to feel more premium.
-------------------------------------------------------------------------- */
const fmtInt = (n) => new Intl.NumberFormat("it-IT").format(n || 0);

const TIER_COLOR = { demo: "#8E8E93", bronze: "#CD7F32", silver: "#B0B7C3", gold: "#F5A524", rookie: "#0A84FF", pro: "#F59E0B" };
const TIER_LABEL = { demo: "Demo", bronze: "Bronze", silver: "Silver", gold: "Gold", rookie: "Rookie", pro: "Pro" };

/* ---- Users by tier — donut with legend & centered total ---------------- */
const UsersByTierChart = ({ data }) => {
  const keys = Object.keys(data || {}).filter((k) => (data[k] || 0) > 0);
  const pie = (keys.length ? keys : ["demo"]).map((k) => ({
    key: k,
    name: TIER_LABEL[k] || k,
    value: data?.[k] || 0,
  }));
  const total = pie.reduce((a, b) => a + b.value, 0);
  return (
    <div className="grid grid-cols-2 gap-3 items-center h-full">
      <div className="relative h-full">
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={pie}
              dataKey="value"
              nameKey="name"
              cx="50%" cy="50%"
              innerRadius="60%" outerRadius="92%"
              paddingAngle={3}
              stroke="#fff"
              strokeWidth={2}
              animationDuration={1000}
              animationBegin={120}
              isAnimationActive
            >
              {pie.map((s) => <Cell key={s.key} fill={TIER_COLOR[s.key] || "#94a3b8"} />)}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : "0.0";
                return (
                  <div className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl">
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-slate-300 mt-0.5">{fmtInt(p.value)} utenti · <span className="text-[#5AC8FA]">{pct}%</span></div>
                  </div>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="font-display font-black text-3xl tracking-tight text-slate-900 leading-none">{fmtInt(total)}</div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-1 font-semibold">Iscritti</div>
          </div>
        </div>
      </div>
      <ul className="space-y-1.5 text-xs">
        {pie.map((s) => {
          const pct = total > 0 ? Math.round((s.value / total) * 100) : 0;
          return (
            <li key={s.key} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: TIER_COLOR[s.key] || "#94a3b8" }} />
              <span className="flex-1 text-slate-700 font-medium">{s.name}</span>
              <span className="font-mono-num text-slate-500 tabular-nums text-[11px]">{fmtInt(s.value)}</span>
              <span className="font-mono-num text-slate-400 text-[10px] w-8 text-right">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/* ---- Signup timeline — cumulative area with hero KPI ------------------- */
const SignupTimelineChart = ({ data }) => {
  const total = data?.length ? data[data.length - 1].cumulative : 0;
  const monthN = data?.length ? data[data.length - 1].count : 0;
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-baseline gap-3 mb-1">
        <div className="font-display font-black text-3xl tracking-tight text-slate-900 leading-none">{fmtInt(total)}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Totale iscritti</div>
        {monthN > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2 py-0.5">
            +{fmtInt(monthN)} questo mese
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 8, right: 4, left: 0, bottom: 4 }}>
            <defs>
              <linearGradient id="ccArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#AF52DE" stopOpacity={0.7} />
                <stop offset="60%" stopColor="#7C3AED" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#AF52DE" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="ccStroke" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#7C3AED" />
                <stop offset="100%" stopColor="#EC4899" />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#eef2f7" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} width={28} />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl">
                    <div className="font-semibold">{label}</div>
                    <div className="text-slate-300 mt-0.5">
                      <span className="text-[#C084FC]">Nuovi:</span> <span className="font-mono-num font-bold">{fmtInt(payload[0]?.payload?.count)}</span>
                    </div>
                    <div className="text-slate-400 text-[10px]">Cumulato: {fmtInt(payload[0]?.payload?.cumulative)}</div>
                  </div>
                );
              }}
            />
            <Area
              type="monotone"
              dataKey="cumulative"
              stroke="url(#ccStroke)"
              strokeWidth={2.5}
              fill="url(#ccArea)"
              animationDuration={1200}
              animationBegin={200}
              dot={false}
              activeDot={{ r: 4, fill: "#7C3AED", strokeWidth: 2, stroke: "#fff" }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/* ---- Weekday activity — vibrant radial rings --------------------------- */
const WeekdayActivityChart = ({ data }) => {
  const colors = ["#0A84FF", "#34C759", "#FF9500", "#AF52DE", "#FF375F", "#5AC8FA", "#F59E0B"];
  const augmented = (data || []).map((d, i) => ({ ...d, fill: colors[i % colors.length] }));
  const total = augmented.reduce((a, b) => a + (b.count || 0), 0);
  const peak = augmented.reduce((a, b) => (b.count > (a?.count || 0) ? b : a), null);
  return (
    <div className="h-full flex flex-col">
      <div className="flex items-baseline gap-3 mb-1">
        <div className="font-display font-black text-3xl tracking-tight text-slate-900 leading-none">{fmtInt(total)}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Immobili tot.</div>
        {peak && peak.count > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold px-2 py-0.5">
            Picco {peak.label}
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer>
          <RadialBarChart
            data={augmented}
            innerRadius="30%"
            outerRadius="100%"
            startAngle={90}
            endAngle={-270}
            cx="50%"
            cy="50%"
          >
            <RadialBar
              dataKey="count"
              background={{ fill: "#f1f5f9" }}
              cornerRadius={8}
              animationDuration={1200}
              animationBegin={250}
              label={{ position: "insideStart", fill: "#fff", fontSize: 10, fontWeight: 700 }}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload;
                return (
                  <div className="bg-slate-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl">
                    <div className="font-semibold">{p.label}</div>
                    <div className="text-slate-300 mt-0.5">{fmtInt(p.count)} immobili inseriti</div>
                  </div>
                );
              }}
            />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/* ---- Card wrapper — gradient accent bar + hover lift ------------------- */
const ChartCard = ({ icon: Icon, title, subtitle, tint, tint2, children, delay = 0, testId }) => (
  <div
    className="ios-card p-5 pt-6 bg-white cc-card-in relative overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
    style={{ animationDelay: `${delay}ms` }}
    data-testid={testId}
  >
    {/* Colored accent bar along the top edge of the card */}
    <div
      className="absolute inset-x-0 top-0 h-1"
      style={{ background: `linear-gradient(90deg, ${tint}, ${tint2 || tint})` }}
    />
    <div className="flex items-start justify-between mb-4">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold">
          <span
            className="inline-flex items-center justify-center w-7 h-7 rounded-xl text-white shadow-sm shrink-0"
            style={{ background: `linear-gradient(135deg, ${tint}, ${tint2 || tint})` }}
          >
            <Icon className="w-4 h-4" strokeWidth={2} />
          </span>
          <span className="truncate">{title}</span>
        </div>
        <div className="text-[13px] text-slate-500 mt-1.5 font-medium">{subtitle}</div>
      </div>
    </div>
    <div className="h-60 min-h-[240px]">{children}</div>
  </div>
);

const CommunityCrossCharts = ({ className = "" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/analytics/community-cross");
        if (!cancelled) setData(r.data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="ios-card p-5 h-80 flex items-center justify-center bg-white">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`} data-testid="community-cross-section">
      <ChartCard icon={Users} tint="#0A84FF" tint2="#5AC8FA" title="Utenti per piano" subtitle="Distribuzione delle tipologie di iscritti" delay={0} testId="cc-tier-mix">
        <UsersByTierChart data={data.users_by_tier} />
      </ChartCard>
      <ChartCard icon={TrendingUp} tint="#AF52DE" tint2="#EC4899" title="Crescita community" subtitle="Iscritti cumulati per mese" delay={140} testId="cc-signup-timeline">
        <SignupTimelineChart data={data.signup_timeline} />
      </ChartCard>
      <ChartCard icon={CalendarDays} tint="#FF9500" tint2="#F59E0B" title="Attività per giorno" subtitle="Quando si mappa di più durante la settimana" delay={280} testId="cc-weekday">
        <WeekdayActivityChart data={data.weekday_activity} />
      </ChartCard>
    </div>
  );
};

export default CommunityCrossCharts;
